import { useEffect, useState } from 'react';
/// <reference types="../types/css-modules" />
import style from '../styles/SignUp.module.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';

/**
 * Multi-step signup form for incoming Community Members
 *
 * Step 1: Personal Identification
 * Step 2: Contact Information
 * Step 3: Account credentials
 */
export function GroupMemberSignUp() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  const [feedback, setFeedback] = useState('');

  const [validating, setValidating] = useState(false);

  const [canValidate, setCanValidate] = useState(false);

  // Initializes form data fields
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    pronouns: '',

    email: '',
    tel: '',

    username: '',
    password: '',
    confirm: '',
  });

  // Initializes password requirements and status
  const passwordValidation = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
    number: /\d/.test(formData.password),
  };

  // Checks a given field for any existing input
  const validateField = (field: string) => {
    return !!formData[field as keyof typeof formData];
  };

  // Checks all fields in a step for existing and valid input
  const validateStep = () => {
    switch (step) {
      case 1:
        return !!(formData.firstName && formData.lastName && formData.pronouns);
      case 2:
        return !!(formData.email && formData.tel && validateEmail());
      case 3:
        return !!(
          formData.username &&
          formData.password &&
          formData.confirm &&
          validatePassword() &&
          formData.password === formData.confirm
        );
    }
  };

  // Checks input password meets all requirements
  const validatePassword = () => {
    return Object.values(passwordValidation).every((value) => value === true);
  };

  // Checks input confirm password matches input password
  const validateConfirm = () => {
    return !!(validatePassword() && formData.password === formData.confirm);
  };

  // Checks input email is in valid format
  const validateEmail = () => {
    const input = document.createElement('input');
    input.type = 'email';
    input.value = formData.email;
    return input.checkValidity();
  };

  // Handles updating form data to new input
  // Updates password validity status by requirement
  const handleFormData = (field: string, data: string) => {
    setFormData((oldData) => ({ ...oldData, [field]: data }));
  };

  // Handles submission to backend and records errors
  const handleSubmit = async () => {
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            display_name: formData.firstName,
            phone: formData.tel,
            user_type: 'member',
          },
        },
      });

      if (signUpError) {
        setFeedback(signUpError.message);
        return;
      }

      const { error: insertError } = await supabase.from('member_profiles').insert({
        id: data.user!.id,
        username: formData.username,
        pronouns: formData.pronouns,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.tel,
      });

      if (insertError) {
        setFeedback(insertError.message);
        return;
      }

      navigate('/tag-selection');
    } catch (err) {
      console.error(err);
      setFeedback('An unexpected error occurred');
    }
  };

  // Handles navigation to the next step of the form
  const handleNext = () => {
    setValidating(true);
    if (validateStep()) {
      if (step === 3) {
        handleSubmit();
      } else {
        setValidating(false);
        setCanValidate(false);
        setStep(step + 1);
      }
    }
  };

  // Handles navigation to the previous step of the form
  const handlePrevious = () => {
    if (step === 1) {
      navigate('/');
    } else {
      setValidating(false);
      setCanValidate(false);
      setStep(step - 1);
    }
  };

  // Delays changes to validating status for each step
  // Prevents fields from immediately appearing as invalid when next button is clicked
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCanValidate(true);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [step]);

  // Displayed component
  return (
    <div className={style.container}>
      {/* Step 1 render */}
      {step === 1 && (
        <div className={style.formContainer}>
          <h2>Tell us a little about yourself</h2>

          <form id="StepOne" className="form">
            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="FirstName">
                  First Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="FirstName"
                  name="FirstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(data) => handleFormData('firstName', data.target.value)}
                  className={`field ${!validateField('firstName') && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="FirstNameReminder"
                className={`${style.reminder}  ${validating && !validateField('firstName') ? '' : style.hidden}`}
              >
                {' '}
                First name is required.
              </p>
            </div>

            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="LastName">
                  Last Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="LastName"
                  name="LastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(data) => handleFormData('lastName', data.target.value)}
                  className={`field ${!validateField('lastName') && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="LastNameReminder"
                className={`${style.reminder}  ${validating && !validateField('lastName') ? '' : style.hidden}`}
              >
                {' '}
                Last name is required.
              </p>
            </div>

            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="Pronouns">
                  Pronouns <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  id="Pronouns"
                  name="Pronouns"
                  value={formData.pronouns}
                  onChange={(data) => handleFormData('pronouns', data.target.value)}
                  className={`field ${!validateField('pronouns') && validating && canValidate ? style.invalid : ''}`}
                >
                  <option value={''}>--Select One--</option>
                  <option value={'He/Him'}>He/Him</option>
                  <option value={'She/Her'}>She/Her</option>
                  <option value={'They/Them'}>They/Them</option>
                  <option value={'Other'}>Other</option>
                </select>
              </div>
              <p
                id="PronounsReminder"
                className={`${style.reminder}  ${validating && !validateField('pronouns') ? '' : style.hidden}`}
              >
                {' '}
                Pronouns are required.
              </p>
            </div>
          </form>
        </div>
      )}

      {/* Step 2 render */}
      {step === 2 && (
        <div className={style.formContainer}>
          <h2>How can we reach you?</h2>

          <form id="StepTwo" className="form">
            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="Email">
                  Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="Email"
                  name="Email"
                  type="email"
                  value={formData.email}
                  onChange={(data) => handleFormData('email', data.target.value)}
                  className={`field ${(!validateField('email') || !validateEmail()) && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="EmailReminder"
                className={`${style.reminder}  ${validating && (!validateField('email') || !validateEmail()) ? '' : style.hidden}`}
              >
                {' '}
                Expected format: group@soup.com.{' '}
              </p>
            </div>

            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="Tel">
                  Phone <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="Tel"
                  name="Tel"
                  type="tel"
                  value={formData.tel}
                  onChange={(data) => handleFormData('tel', data.target.value)}
                  className={`field ${!validateField('tel') && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="TelReminder"
                className={`${style.reminder}  ${validating && !validateField('tel') ? '' : style.hidden}`}
              >
                {' '}
                Phone is required.{' '}
              </p>
            </div>
          </form>
        </div>
      )}

      {/* Step 3 render */}
      {step === 3 && (
        <div className={style.formContainer}>
          <h2>Create a login</h2>

          <form id="StepThree" className="form">
            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="Username">
                  Username <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="Username"
                  name="Username"
                  type="text"
                  value={formData.username}
                  onChange={(data) => handleFormData('username', data.target.value)}
                  className={`field ${!validateField('username') && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="UsernameReminder"
                className={`${style.reminder}  ${validating && !validateField('username') ? '' : style.hidden}`}
              >
                {' '}
                Username is required.
              </p>
            </div>

            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="Password">
                  Password <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="Password"
                  name="Password"
                  type="password"
                  onChange={(data) => handleFormData('password', data.target.value)}
                  className={`field ${(!validateField('password') || !validatePassword()) && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="PasswordReminder"
                className={`${style.reminder}  ${validating && (!validateField('password') || !validatePassword()) ? '' : style.hidden}`}
              >
                {' '}
                Invalid password.{' '}
              </p>
            </div>

            <div className="fieldContainer">
              <div className="fieldWithLabel">
                <label htmlFor="Confirm">
                  Confirm <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="Confirm"
                  name="Confirm"
                  type="password"
                  onChange={(data) => handleFormData('confirm', data.target.value)}
                  className={`field ${(!validateField('confirm') || !validateConfirm()) && validating && canValidate ? style.invalid : ''}`}
                ></input>
              </div>
              <p
                id="ConfirmReminder"
                className={`${style.reminder}  ${validating && (!validateField('confirm') || !validateConfirm()) ? '' : style.hidden}`}
              >
                {' '}
                Confirmation failed.{' '}
              </p>
            </div>

            <p
              className={` ${style.passwordGuide} ${validatePassword() ? style.guideLineMet : ''} `}
            >
              <strong>Password must contain at least:</strong>
            </p>
            <p
              className={`${style.passwordGuide} ${passwordValidation.length ? style.guideLineMet : ''}`}
            >
              {' '}
              8 characters{' '}
            </p>
            <p
              className={`${style.passwordGuide} ${passwordValidation.special ? style.guideLineMet : ''}`}
            >
              {' '}
              1 special character{' '}
            </p>
            <p
              className={`${style.passwordGuide} ${passwordValidation.number ? style.guideLineMet : ''}`}
            >
              {' '}
              1 number{' '}
            </p>
            <p
              className={`${style.passwordGuide} ${passwordValidation.uppercase ? style.guideLineMet : ''}`}
            >
              {' '}
              1 uppercase letter{' '}
            </p>
            <p
              className={`${style.passwordGuide} ${passwordValidation.lowercase ? style.guideLineMet : ''}`}
            >
              {' '}
              1 lowercase letter
            </p>
          </form>

          <p>{feedback}</p>
        </div>
      )}

      {/* Navigation render */}
      <div className={style.buttonContainer}>
        {step === 1 && (
          <button
            aria-label="backButton"
            onClick={handlePrevious}
            className={`${style.button} ${style.back}`}
          ></button>
        )}

        {(step === 2 || step === 3) && (
          <button
            aria-label="previousButton"
            onClick={handlePrevious}
            className={`${style.button} ${style.previous}`}
          ></button>
        )}

        {/* Updates field validity status (for feedback) on hover */}
        {(step === 1 || step === 2) && (
          <button
            aria-label="nextButton"
            onClick={handleNext}
            className={`${style.button} ${style.next} ${!validateStep() ? style.unavailable : ''}`}
          ></button>
        )}

        {step === 3 && (
          <button
            aria-label="finishButton"
            onClick={handleNext}
            className={`${style.button} ${style.finish} ${!validateStep() ? style.unavailable : ''}`}
          >
            {' '}
            Finish{' '}
          </button>
        )}
      </div>
    </div>
  );
}
